import React from "react";
import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  NumberInput,
  BooleanInput,
  FormDataConsumer,
  required,
  maxLength,
  Toolbar,
  SaveButton,
  DeleteButton,
  useEditContext,
  useNotify,
  useRedirect,
} from "react-admin";
import { Box, Typography } from "@mui/material";
import FirebaseImageInput from "../../components/FirebaseImageInput";
import ColorInput from "../../components/ColorInput";
import { ChannelActionType } from "../../types";

// Kept to one line under the channel title on phone-width cards.
const SUBTITLE_MAX_LENGTH = 60;

const actionTypeChoices = [
  { id: ChannelActionType.HANDS_UP, name: "Hands Up" },
  { id: ChannelActionType.CHAT, name: "Chat" },
  { id: ChannelActionType.DISABLED, name: "Disabled" },
];

const messageTimeoutChoices = [
  { id: 0, name: "No limit" },
  { id: 900, name: "15 minutes" },
  { id: 1800, name: "30 minutes" },
  { id: 3600, name: "1 hour" },
  { id: 10800, name: "3 hours" },
  { id: 21600, name: "6 hours" },
  { id: 43200, name: "12 hours" },
  { id: 86400, name: "1 day" },
  { id: 604800, name: "1 week" },
  { id: 2592000, name: "1 month" },
];

const ChannelEditToolbar = (props: any) => (
  <Toolbar {...props} sx={{ display: "flex", justifyContent: "space-between" }}>
    <SaveButton />
    <DeleteButton redirect="/channels" />
  </Toolbar>
);

const CustomEditForm: React.FC<any> = (props) => {
  const { record } = useEditContext();
  if (!record) return <div>Loading...</div>;

  const computedRecord = {
    ...record,
    channelImage: record.assets?.[0] || undefined,
    // "No limit" is stored as null, never 0: the app turns any non-null timeout into a
    // createdAt cutoff, so 0 would hide the whole chat history. react-admin skips
    // defaultValue for a null field, which left the required select empty on every edit,
    // so null is shown as the "No limit" choice here and the transform saves it as null.
    messageTimeout: record.messageTimeout ?? 0,
  };

  return (
    <SimpleForm {...props} record={computedRecord} />
  );
};

const ChannelEdit: React.FC = () => {
  const notify = useNotify();
  const redirect = useRedirect();

  const transform = (data: any) => ({
    id: data.id,
    title: data.title,
    wave: data.wave,
    streamUrl: data.streamUrl,
    channelId: data.channelId,
    subTitle: data.subTitle?.trim() || null,
    description: data.description || "",
    actionType: data.actionType || ChannelActionType.DISABLED,
    priority: data.priority ?? 0,
    isHidden: data.isHidden ?? false,
    messageTimeout: data.actionType === ChannelActionType.CHAT ? (data.messageTimeout || null) : null,
    colorHex: data.colorHex || null,
    assets: data.channelImage?.url ? [data.channelImage] : (data.assets || []),
  });

  const onSuccess = () => {
    notify("channelsPage.updateSuccess");
    redirect("/channels");
  };

  const onError = () => {
    notify("channelsPage.updateError", { type: "error" });
  };

  return (
    <Edit
      resource="sync/override/channels"
      transform={transform}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError }}
    >
      <CustomEditForm toolbar={<ChannelEditToolbar />}>
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            ⚠️ Please note: After editing a channel, it may take up to 24 hours
            for the changes to appear in the app or on the website due to
            caching.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            Changes will be reflected in the app after the next sync cycle
            (up to 6 hours) or you can trigger a manual sync from the
            dashboard.
          </Typography>
        </Box>
        <TextInput source="title" label="Title" fullWidth validate={required()} />
        <TextInput
          source="subTitle"
          label="Subtitle"
          fullWidth
          validate={maxLength(SUBTITLE_MAX_LENGTH)}
          helperText={`Short tagline shown under the channel title in the apps, e.g. "House & techno, 24/7". Max ${SUBTITLE_MAX_LENGTH} characters.`}
        />
        <TextInput source="channelId" label="Channel ID" fullWidth validate={required()} />
        <TextInput source="wave" label="Wave" fullWidth sx={{ display: "none" }} />
        <TextInput source="streamUrl" label="Stream URL" fullWidth validate={required()} />
        <TextInput source="description" label="Description" fullWidth multiline rows={3} />
        <SelectInput
          source="actionType"
          label="Action Type"
          choices={actionTypeChoices}
          defaultValue={ChannelActionType.DISABLED}
          validate={required()}
          helperText="Controls user interaction: Disabled = no action, Chat = live chat, Hands Up = hands up icon & functionality."
          fullWidth
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.actionType === ChannelActionType.CHAT ? (
              <SelectInput
                source="messageTimeout"
                label="Chat Message Timeout"
                choices={messageTimeoutChoices}
                defaultValue={0}
                validate={required()}
                helperText="Time before live chat messages disappear in the app."
              />
            ) : null
          }
        </FormDataConsumer>
        <NumberInput
          source="priority"
          label="Priority"
          defaultValue={0}
          helperText="Lower number = higher priority (shown first). Defaults to 0."
        />
        <BooleanInput
          source="isHidden"
          label="Hidden"
          helperText="Hidden channels are excluded from the public app on the next sync."
        />
        <ColorInput
          source="colorHex"
          label="Background Colour"
          helperText="Background colour used by the apps for this radio. Leave empty to use the app default."
        />
        <Box mt={2}>
          <FirebaseImageInput source="channelImage" label="Channel Image" storagePath="channels" />
        </Box>
      </CustomEditForm>
    </Edit>
  );
};

export default ChannelEdit;
